import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useFormik } from 'formik'
import { useNavigate, useParams } from 'react-router-dom'
import { Box, Typography } from '@mui/material'

/* Utils */
import axios from 'lib/axios'
import { defaultValues } from '../setupIntegration.constants'
import { useAppDispatch } from 'store/hooks'
import {
  IntegrationInstance,
  JiraDataMapping,
  ManifestIntegration,
  ManifestIntegrationSupportedCPI,
  ProductOnboardingView,
  ProductSpec,
  ProductSpecConnectionConfig,
  SnowflakeInetgrationSchemaItem
} from 'models'
import { stringOfNumberedListToArray } from 'lib/utils'
import { closeToast, openToast, ToastVariant } from 'store/toasts/toastSlice'
import { ModalType, openModal } from 'store/modals/modalSlice'
import { ProductsServiceV4 } from 'services/v4/products-v4.service'
import { buildCpiActivationOffer } from '../utils'

/* Components */
import { IntegrationsContainer } from '../setupIntegrationInstance.styles'
import { FormColumn, RightPane, WizardBody } from './jiraWizard.styles'
import JiraHeader from './JiraHeader'
import { useJiraOnboarding } from './onboarding-wizard/useJiraOnboarding'
import {
  findStep,
  isConnectorSetupCompleted,
  isStepUnlocked,
  resolveContextVariants,
  resolveSpecStep
} from './onboarding-wizard/wizard.utils'
import { PREVIEW_EMPTY_STATES, WIZARD_STEPS, stepIndexOf } from './onboarding-wizard/wizard.constants'
import WizardStepper from './onboarding-wizard/components/WizardStepper'
import WizardSectionHeader from './onboarding-wizard/components/WizardSectionHeader'
import WizardFooter from './onboarding-wizard/components/WizardFooter'
import InstructionsPane from './onboarding-wizard/components/InstructionsPane'
import PreviewEmptyState from './onboarding-wizard/components/PreviewEmptyState'
import ConnectionDetailsStep from './onboarding-wizard/steps/ConnectionDetailsStep'
import ConnectorDomainStep from './onboarding-wizard/steps/ConnectorDomainStep'
import ProjectSelectionStep from './onboarding-wizard/steps/ProjectSelectionStep'
import DataFilteringStep from './onboarding-wizard/steps/DataFilteringStep'
import DataMappingStep from './onboarding-wizard/steps/DataMappingStep'
import FilterPreviewPane from './onboarding-wizard/data-configuration/preview/FilterPreviewPane'
import {
  FilterDraftRow,
  emptyFilterRow,
  filtersPayload,
  hasIncompleteRow,
  isMappingIncomplete,
  mappingPayload,
  toDraftRows
} from './onboarding-wizard/data-configuration/dataConfiguration.utils'
import { useJiraFilterPreview } from './onboarding-wizard/data-configuration/useJiraFilterPreview'
import {
  FILTER_RESOURCES,
  MAPPING_RESOURCES,
  PRIORITIES_RESOURCE,
  STATUSES_RESOURCE,
  filterOptionsFor,
  optionSource,
  useJiraFieldsCatalog,
  useJiraOptionSources
} from './onboarding-wizard/data-configuration/useJiraOptions'

const INTEGRATION_NAME = 'atlassian_jira'

const LAST_STEP = WIZARD_STEPS.length - 1

const NAME_PATTERN = /^[0-9a-zA-Z\-_]+$/

const DUPLICATE_NAME_HELPER = 'An instance with this name already exists'

/* The fallback for a step the server rejected without naming a reason. */
const GENERIC_STEP_ERROR = 'The server did not accept this step. Please review your selection and try again.'

/* The Jira add wizard: five steps in a left rail, one step's fields at a time,
 * each submitted to the product onboarding API by the footer's Next. */
const SetupJiraInstance: FC = () => {
  const params = useParams()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const closeNotification = useRef<any>(null)

  const [integration, setIntegration] = useState<ManifestIntegration | null>(null)
  const [productSpec, setProductSpec] = useState<ProductSpec | null>(null)
  const [configSchemaV2, setSchemaV2] = useState<SnowflakeInetgrationSchemaItem[]>([])
  const [integrationLoading, setIntegrationLoading] = useState(true)
  const [dataLoading, setDataLoading] = useState(true)
  const [instructions, setInstructions] = useState<RegExpMatchArray | null>(null)
  const [visibleValues, setVisibleValues] = useState<Record<string, boolean>>({})
  const [nameHelperText, setNameHelperText] = useState('')
  const [existingInstances, setExistingInstances] = useState<IntegrationInstance[]>([])
  const [initialValues, setInitialValues] = useState<Record<string, string>>(defaultValues)

  const [activeStep, setActiveStep] = useState(0)
  const [stepError, setStepError] = useState('')
  const [busy, setBusy] = useState(false)

  /* A step handler that knows why it failed names the reason here; Next reads it
   * back after the attempt, since the state it also sets is not readable yet.
   * null means the field already carries the reason. */
  const stepFailure = useRef<string | null>('')

  const failStep = (message: string) => {
    stepFailure.current = message
    setStepError(message)
  }

  /* The offending field shows the reason itself, as the other integrations do,
   * so the section stays silent rather than repeating it. */
  const failStepOnField = () => {
    stepFailure.current = null
    setStepError('')
  }

  /* Step drafts live here so they survive moving between steps. */
  const [domain, setDomain] = useState('')
  const [projectKey, setProjectKey] = useState('')
  const [rows, setRows] = useState<FilterDraftRow[]>([emptyFilterRow()])
  const [mapping, setMapping] = useState<JiraDataMapping>({})

  const { view, applying, createOnboarding, submitStep, completeOnboarding, loadView, isStepCompleted } =
    useJiraOnboarding()

  const resuming = !!params.instanceId
  const stepDefinition = WIZARD_STEPS[activeStep]
  const connectorSetupDone = isConnectorSetupCompleted(view)
  const mappingUnlocked = connectorSetupDone && isStepUnlocked(view, 'data_mapping')

  /* Rehydrate the drafts from the steps the server already holds. */
  const appliedConnection = findStep(view, 'init')?.data
  const appliedDomain = findStep(view, 'connector_domain')?.data?.context_type
  const appliedProjectKey = findStep(view, 'connected_projects')?.data?.projects?.[0]?.key
  const appliedFilters = findStep(view, 'data_filters')?.data?.filters
  const appliedMapping = findStep(view, 'data_mapping')?.data as JiraDataMapping | undefined

  /* Every Jira catalog is served for the connected project, so each one is scoped
   * to it — the applied project is what makes their contents change. */
  const { catalog, loading: catalogLoading } = useJiraFieldsCatalog(
    view?.product_id,
    connectorSetupDone,
    appliedProjectKey
  )
  const { sources: filterSources, loading: filterOptionsLoading } = useJiraOptionSources(
    view?.product_id,
    FILTER_RESOURCES,
    connectorSetupDone,
    appliedProjectKey
  )
  const { sources: mappingSources, loading: mappingOptionsLoading } = useJiraOptionSources(
    view?.product_id,
    MAPPING_RESOURCES,
    mappingUnlocked,
    appliedProjectKey
  )
  const { preview, loading: previewLoading, error: previewError, runPreview } = useJiraFilterPreview(view?.product_id)

  const mappingFields = catalog?.mapping_fields ?? []
  const rowsReady = !!rows.length && !hasIncompleteRow(rows)

  const mappingStatuses = optionSource(mappingSources, STATUSES_RESOURCE)
  const mappingPriorities = optionSource(mappingSources, PRIORITIES_RESOURCE)

  /* Whether a step may be finished empty is manifest-owned. Absent a flag the
   * step is treated as required, so the gate never loosens by accident. */
  const mappingRequired = resolveSpecStep(productSpec, 'data_mapping')?.required !== false
  const mappingEmpty = isMappingIncomplete(mapping, mappingFields)

  const formik = useFormik({ initialValues, enableReinitialize: true, onSubmit: () => undefined })

  /* A rejection that names the instance name goes stale the moment it is edited. */
  useEffect(() => {
    setStepError('')
  }, [formik.values.name])

  const notify = (variant: ToastVariant, text: string, description = '') => {
    dispatch(openToast({ variant, props: { text, description } }))
    closeNotification.current = setTimeout(() => dispatch(closeToast()), 5000)
  }

  /* The connection form renders the spec's required connection properties. */
  const transformPropertiesToArray = (connectionConfig: ProductSpecConnectionConfig) => {
    const { properties, required } = connectionConfig

    return Object.entries(properties)
      .map(([key, value]) => ({ name: key, required: required.includes(key), ...(value as any) }))
      .filter((property) => property.required === true)
  }

  const fetchIntegrations = useCallback(async () => {
    try {
      const res = await axios.get('/api/v3/integrations/supported')
      if (res.status !== 200) return

      const found = res.data.integrations.find((n: ManifestIntegration) => n.name === INTEGRATION_NAME)
      if (!found) return

      setIntegration(found)

      const spec = await ProductsServiceV4.getSpec(found.name)
      setProductSpec(spec)

      const schema = spec ? transformPropertiesToArray(spec.connection_config) : []
      setSchemaV2(schema)

      const instanceValues: Record<string, string> = {
        name: spec?.manifest?.slug || found.name,
        description: spec?.manifest?.description || ''
      }
      const secretFields: Record<string, boolean> = {}
      schema.forEach((property) => {
        instanceValues[property.name] = ''
        if (property.airbyte_secret) secretFields[property.name] = true
      })

      setVisibleValues(secretFields)

      /* Seeding runs again once the instance lands in the URL, so it merges over
       * what the form already holds rather than blanking a filled-in step. */
      setInitialValues((current) => ({
        ...instanceValues,
        ...Object.fromEntries(Object.entries(current).filter(([, value]) => !!value))
      }))
    } catch (e) {
      console.error(e)
      if ((e as any).response?.data?.message === 'Unauthorized') window.location.replace('/')
    }
  }, [])

  const fetchExistingInstances = async () => {
    try {
      const res = await axios.get<{ instances: IntegrationInstance[] }>(
        '/api/v3/integrations/configured?loadStatus=true'
      )
      if (res.status === 200) setExistingInstances(res.data.instances)
    } catch (e) {
      console.error(e)
    }
  }

  const fetchData = async () => {
    setDataLoading(true)

    try {
      await fetchIntegrations()
      if (params.instanceId) {
        await loadView(params.instanceId)
      } else {
        await fetchExistingInstances()
      }
    } catch (e) {
      console.error(e)
    } finally {
      setDataLoading(false)
    }
  }

  useEffect(() => {
    fetchData().catch((e) => e)
  }, [params.instanceId])

  useEffect(() => {
    if (!integration) return

    if (integration?.documentation?.path) {
      import(`../instructions/${integration?.documentation?.path}_instructions.md`)
        .then((res) => {
          fetch(res.default)
            .then((res) => res.text())
            .then((res) => setInstructions(stringOfNumberedListToArray(res, integration.name)))
            .catch((err) => console.error(err))
            .finally(() => setIntegrationLoading(false))
        })
        .catch((err) => console.error(err))
    } else {
      setTimeout(() => setIntegrationLoading(false), 200)
    }
  }, [integration])

  /* A resumed wizard opens on the server's current step. A fresh one advances
   * step by step, so it must not be pulled back by the loaded view. */
  const landed = useRef(false)
  useEffect(() => {
    if (!view || landed.current || !resuming) return
    landed.current = true
    setActiveStep(stepIndexOf(view.current_step))
  }, [view, resuming])

  /* The connection properties the server hands back for the applied step. A
   * property it withholds — a secret it will not re-serve — stays blank, and the
   * field can be re-entered. */
  useEffect(() => {
    if (!appliedConnection || !configSchemaV2.length) return

    const saved = Object.fromEntries(
      configSchemaV2
        .map((property) => [property.name, appliedConnection[property.name]])
        .filter(([, value]) => typeof value === 'string' && !!value)
    )

    if (!Object.keys(saved).length) return

    setInitialValues((current) => ({ ...current, ...saved }))
  }, [appliedConnection, configSchemaV2])

  useEffect(() => {
    if (appliedDomain) setDomain(appliedDomain)
  }, [appliedDomain])

  useEffect(() => {
    if (appliedProjectKey) setProjectKey(appliedProjectKey)
  }, [appliedProjectKey])

  /* Filters and mapping hold values drawn from the project they were built
   * against, and re-submitting the project step drops both server-side. Moving
   * to a different project clears the drafts so the section rebuilds on the new
   * project's values rather than submitting the old project's ids. */
  const draftedProjectKey = useRef<string | undefined>()
  useEffect(() => {
    const previous = draftedProjectKey.current
    if (appliedProjectKey) draftedProjectKey.current = appliedProjectKey

    if (!previous || !appliedProjectKey || previous === appliedProjectKey) return

    setRows([emptyFilterRow()])
    setMapping({})
  }, [appliedProjectKey])

  useEffect(() => {
    if (appliedFilters?.length) setRows(toDraftRows(appliedFilters))
  }, [appliedFilters])

  useEffect(() => {
    if (appliedMapping && Object.keys(appliedMapping).length) setMapping(appliedMapping)
  }, [appliedMapping])

  const isDuplicateName = (name: string) =>
    existingInstances.some(
      (instance) => instance.name.toLowerCase() === name.toLowerCase() && instance.integration_name === INTEGRATION_NAME
    )

  /* The name rules, in the order the field reports them. A duplicate is caught
   * while typing rather than on Next, so the field carries the whole story and
   * Next stays disabled — the behaviour the other integrations already have. */
  const validateName = (value: string) => {
    if (!value.length) return 'Please enter a name for the instance'
    if (!value.match(NAME_PATTERN)) return 'Only alphanumeric, dash and hyphen characters are allowed'
    if (isDuplicateName(value)) return DUPLICATE_NAME_HELPER

    return ''
  }

  /* Step 1 has no Test button: Next verifies the credentials, then creates the
   * onboarding that unlocks the rest of the wizard. */
  const submitConnection = async () => {
    if (!productSpec) return false
    if (isStepCompleted('init')) return true

    if (isDuplicateName(formik.values.name)) {
      setNameHelperText(DUPLICATE_NAME_HELPER)
      formik.setFieldError('name', DUPLICATE_NAME_HELPER)
      failStepOnField()
      return false
    }

    const connectionConfig = { ...formik.values }
    delete connectionConfig.name
    delete connectionConfig.description

    const res = await axios.post('/api/v4/products/check_connection', {
      product_check: { product_slug: productSpec.manifest.slug, connection_config: connectionConfig }
    })

    if (res.status !== 200) {
      failStep('Connection test failed. Check your network and settings.')
      return false
    }

    const created = await createOnboarding({
      product_slug: productSpec.manifest.slug,
      display_name: formik.values.name,
      description: formik.values.description,
      data: connectionConfig
    })

    /* Reflect the instance in the URL for refresh/resume without remounting the
     * page — advancing must not reload the wizard. */
    if (created?.instance_name) {
      window.history.replaceState(null, '', `/settings/integrations/data-sources/${INTEGRATION_NAME}/${created.instance_name}/add`)
    }

    return true
  }

  /* The wizard's ending: completing provisions the connector and starts the first
   * sync, then the standard add-CPIs dialog offers to attach the product to its
   * supported CPIs. Either choice lands on the integrations list. */
  const handleComplete = async () => {
    clearTimeout(closeNotification.current)

    const completed = await completeOnboarding()
    const instanceName = completed?.instance_name || view?.instance_name || ''

    notify(ToastVariant.success, 'Integration changes were saved successfully!', `${integration?.display_name}`)

    const addCpisCallback = async (cpis: string[]) => {
      if (!cpis.length) return

      try {
        await ProductsServiceV4.configureMetrics(instanceName, cpis)
        notify(ToastVariant.success, `${integration?.display_name} was successfully added to ${cpis.length} CPIs.`)
      } catch (e) {
        console.error(e)
        notify(
          ToastVariant.error,
          `Failed to add ${integration?.display_name} to ${cpis.length} CPIs.`,
          (e as any).response?.data?.message || ''
        )
      }
    }

    /* Setup is already complete here, so a failed offer fetch fails soft: the
     * CPIs stay activatable from the CPI library. */
    let cpisList: ManifestIntegrationSupportedCPI[] | null = null
    try {
      const metrics = await ProductsServiceV4.getCompatibleMetrics(instanceName)
      cpisList = buildCpiActivationOffer(metrics)
    } catch (e) {
      console.error(e)
      notify(
        ToastVariant.error,
        'Failed to load CPIs for activation',
        (e as any).response?.data?.message || 'CPIs can be activated later from the CPI Library'
      )
    }

    if (!cpisList) {
      navigate('/settings/integrations/data-sources')
      return
    }

    dispatch(
      openModal({
        type: ModalType.addCpisToIntegration,
        props: {
          integrationName: formik.values.name || '',
          cpisList,
          addCpisCallback,
          cancelNavigation: () => navigate('/settings/integrations/data-sources')
        }
      })
    )
  }

  /* The step is only done when the returned view says so — a step the server
   * leaves open must not be stepped past. */
  const isSettled = (updated: ProductOnboardingView | undefined, key: string) => {
    const status = updated?.steps?.find((step) => step.key === key)?.status

    return status === 'completed' || status === 'skipped'
  }

  /* Re-submitting an unchanged step would reset the ones after it server-side, so
   * a completed step whose draft still matches just advances. */
  const submitCurrentStep = async () => {
    const key = stepDefinition.key

    switch (key) {
      case 'init':
        return submitConnection()
      case 'connector_domain':
        if (isStepCompleted(key) && appliedDomain === domain) return true
        return isSettled(await submitStep(key, { context_type: domain }), key)
      case 'connected_projects':
        if (isStepCompleted(key) && appliedProjectKey === projectKey) return true
        return isSettled(await submitStep(key, { project_keys: [projectKey] }), key)
      case 'data_filters': {
        const payload = filtersPayload(rows)
        if (isStepCompleted(key) && JSON.stringify(appliedFilters) === JSON.stringify(payload)) return true
        return isSettled(await submitStep(key, { filters: payload }), key)
      }
      case 'data_mapping': {
        /* Submitting bumps the revision, and completing must carry the new one —
         * the onboarding hook reads it from the response, not from state. */
        const updated =
          mappingEmpty && !mappingRequired
            ? await submitStep(key, {}, true)
            : await submitStep(key, mappingPayload(mapping, mappingFields))
        if (!isSettled(updated, key)) return false

        await handleComplete()
        return true
      }
      default:
        return true
    }
  }

  const handleNext = async () => {
    setStepError('')
    stepFailure.current = ''
    setBusy(true)

    try {
      const advanced = await submitCurrentStep()

      if (!advanced) {
        if (stepFailure.current !== null) setStepError(stepFailure.current || GENERIC_STEP_ERROR)
        return
      }

      if (activeStep < LAST_STEP) setActiveStep(activeStep + 1)
    } catch (e: any) {
      console.error(e)
      setStepError(e?.response?.data?.message || 'Something went wrong. Please review your selection and try again.')
    } finally {
      setBusy(false)
    }
  }

  /* Leaving a step without submitting discards its draft, so the wizard never
   * shows an unsaved domain or project while the sections after it still
   * describe the applied one. A value not yet applied has nothing to fall back
   * to, so the in-progress choice stands. */
  const discardUnsubmittedDraft = () => {
    if (stepDefinition.key === 'connector_domain' && appliedDomain) setDomain(appliedDomain)
    if (stepDefinition.key === 'connected_projects' && appliedProjectKey) setProjectKey(appliedProjectKey)
  }

  const goToStep = (index: number) => {
    setStepError('')
    discardUnsubmittedDraft()
    setActiveStep(index)
  }

  const handleBack = () => {
    setStepError('')
    if (activeStep === 0) {
      navigate('/settings/integrations/data-sources')
      return
    }
    discardUnsubmittedDraft()
    setActiveStep(activeStep - 1)
  }

  const connectionIncomplete = useMemo(() => {
    if (!formik.values.name || nameHelperText.length) return true

    return configSchemaV2.some((property) => property.required && !formik.values[property.name])
  }, [formik.values, nameHelperText, configSchemaV2])

  const nextDisabled = useMemo(() => {
    if (busy || applying) return true

    switch (stepDefinition.key) {
      case 'init':
        return connectionIncomplete
      case 'connector_domain':
        return !domain
      case 'connected_projects':
        return !projectKey
      case 'data_filters':
        return hasIncompleteRow(rows)
      case 'data_mapping':
        return mappingRequired && mappingEmpty
      default:
        return false
    }
  }, [busy, applying, stepDefinition, connectionIncomplete, domain, projectKey, rows, mappingRequired, mappingEmpty])

  const completedKeys = WIZARD_STEPS.filter((step) => isStepCompleted(step.key)).map((step) => step.key)

  if (!integration || dataLoading) return <IntegrationsContainer />

  const renderStep = () => {
    switch (stepDefinition.key) {
      case 'init':
        return (
          <ConnectionDetailsStep
            formik={formik}
            schema={configSchemaV2}
            visibleValues={visibleValues}
            setVisibleValues={setVisibleValues}
            nameHelperText={nameHelperText}
            setNameHelperText={setNameHelperText}
            validateName={validateName}
            disabled={busy || isStepCompleted('init')}
          />
        )
      case 'connector_domain':
        return (
          <ConnectorDomainStep
            variants={resolveContextVariants(productSpec)}
            value={domain}
            disabled={busy}
            onChange={setDomain}
          />
        )
      case 'connected_projects':
        return (
          <ProjectSelectionStep
            productId={view?.product_id}
            value={projectKey}
            disabled={busy}
            onChange={setProjectKey}
          />
        )
      case 'data_filters':
        return (
          <DataFilteringStep
            rows={rows}
            onRowsChange={setRows}
            fields={catalog?.fields ?? []}
            operators={catalog?.operators ?? []}
            optionsFor={(jiraField) => filterOptionsFor(filterSources, jiraField)}
            loadingOptions={filterOptionsLoading}
            disabled={busy}
            previewing={previewLoading}
            previewDisabled={previewLoading || !rowsReady}
            onPreview={() => runPreview(filtersPayload(rows))}
          />
        )
      case 'data_mapping':
        return (
          <DataMappingStep
            mapping={mapping}
            onMappingChange={setMapping}
            mappingFields={mappingFields}
            statuses={mappingStatuses}
            priorities={mappingPriorities}
            loadingOptions={mappingOptionsLoading}
            disabled={busy}
            locked={!mappingUnlocked}
          />
        )
      default:
        return null
    }
  }

  const renderRightPane = () => {
    if (stepDefinition.key === 'init') {
      return <InstructionsPane integration={integration} instructions={instructions} loading={integrationLoading} />
    }

    const emptyState = PREVIEW_EMPTY_STATES[stepDefinition.key]
    if (emptyState) return <PreviewEmptyState title={emptyState.title} description={emptyState.description} />

    return <FilterPreviewPane preview={preview} loading={previewLoading} error={previewError} />
  }

  return (
    <IntegrationsContainer>
      <JiraHeader
        headerTitle={`${resuming ? 'Edit' : 'Add'} ${integration.display_name} Integration Instance`}
        handleBack={() => navigate('/settings/integrations/data-sources')}
      />

      <WizardBody>
        <WizardStepper
          steps={WIZARD_STEPS}
          activeStep={activeStep}
          completedKeys={completedKeys}
          onStepClick={goToStep}
        />

        <FormColumn>
          <Box className="form-scroll">
            <WizardSectionHeader title={stepDefinition.title} description={stepDefinition.description} />
            {renderStep()}
            {!!stepError && <Typography className="section-error">{stepError}</Typography>}
            {!catalogLoading && connectorSetupDone && !catalog && (
              <Typography className="section-hint">
                Filter and mapping fields could not be loaded from Jira. Please try again later.
              </Typography>
            )}
          </Box>

          <WizardFooter
            backLabel={activeStep === 0 ? 'Cancel' : 'Back'}
            nextLabel={activeStep === LAST_STEP ? 'Save' : 'Next'}
            nextDisabled={nextDisabled}
            submitting={busy || applying}
            onBack={handleBack}
            onNext={handleNext}
          />
        </FormColumn>

        <RightPane>{renderRightPane()}</RightPane>
      </WizardBody>
    </IntegrationsContainer>
  )
}

export default SetupJiraInstance
